import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, PlayCircle, Receipt } from "lucide-react";
import { useState } from "react";

import { AuditTraceViewer } from "../components/AuditTraceViewer";
import { ErrorPanel, Loading } from "../components/Loading";
import { api, ApiError } from "../lib/api";
import { formatMoney } from "../lib/format";
import { useLocale } from "../lib/i18n";
import type { Payslip, PayrollRunResponse } from "../lib/types";

function PayslipRow({ payslip }: { payslip: Payslip }) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="hover:bg-slate-50">
        <td className="px-6 py-3 font-mono text-xs text-slate-600">{payslip.employee_code}</td>
        <td className="px-6 py-3 font-medium text-slate-900">{payslip.employee_name_en}</td>
        <td className="px-6 py-3 text-end font-mono tabular-nums text-slate-800">
          {formatMoney(payslip.gross_pay)}
        </td>
        <td className="px-6 py-3 text-end font-mono tabular-nums text-rose-600">
          −{formatMoney(payslip.gosi_employee_deduction)}
        </td>
        <td className="px-6 py-3 text-end font-mono tabular-nums text-rose-600">
          −{formatMoney(payslip.loan_deductions)}
        </td>
        <td className="px-6 py-3 text-end font-mono font-semibold tabular-nums text-emerald-700">
          {formatMoney(payslip.net_pay)}
        </td>
        <td className="px-6 py-3 text-end">
          {payslip.audit_trace ? (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
            >
              {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              {t.payroll_view_trace}
            </button>
          ) : null}
        </td>
      </tr>
      {open && payslip.audit_trace ? (
        <tr>
          <td colSpan={7} className="bg-slate-50 px-6 py-4">
            <AuditTraceViewer trace={payslip.audit_trace} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function PayslipsTable({ payslips }: { payslips: Payslip[] }) {
  const { t } = useLocale();
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <table className="min-w-full divide-y divide-slate-100">
        <thead className="bg-slate-50">
          <tr className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            <th className="px-6 py-3 text-start">{t.col_code}</th>
            <th className="px-6 py-3 text-start">{t.payroll_col_employee}</th>
            <th className="px-6 py-3 text-end">{t.payroll_col_gross}</th>
            <th className="px-6 py-3 text-end">{t.payroll_col_gosi}</th>
            <th className="px-6 py-3 text-end">{t.payroll_col_loan}</th>
            <th className="px-6 py-3 text-end">{t.payroll_col_net}</th>
            <th className="px-6 py-3 text-end" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 text-sm">
          {payslips.map((p) => (
            <PayslipRow key={p.employee_id} payslip={p} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PayrollSummary({ run }: { run: PayrollRunResponse }) {
  const { t } = useLocale();
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">
            {t.payroll_total}
          </p>
          <p className="mt-1 font-mono text-3xl font-bold tabular-nums text-slate-900">
            {formatMoney(run.total_amount_paid)}
          </p>
          <p className="mt-1 text-sm text-slate-600">
            {run.pay_period_start} — {run.pay_period_end} · {run.total_employees} {t.col_name.toLowerCase()}
          </p>
        </div>
        <div className="text-end text-xs font-mono text-slate-500">
          run id<br />
          <span className="text-slate-700">{run.payroll_run_id}</span>
        </div>
      </div>
    </div>
  );
}

export function PayrollPage() {
  const { t } = useLocale();
  const customersQ = useQuery({
    queryKey: ["customers"],
    queryFn: () => api.listCustomers(),
  });
  const customer = customersQ.data?.[0];

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const toIso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const [periodStart, setPeriodStart] = useState(toIso(firstOfMonth));
  const [periodEnd, setPeriodEnd] = useState(toIso(lastOfMonth));

  const runM = useMutation<PayrollRunResponse, ApiError>({
    mutationFn: () =>
      api.runPayroll({
        customer_id: customer!.id,
        pay_period_start: periodStart,
        pay_period_end: periodEnd,
      }),
  });

  if (customersQ.isPending) return <Loading />;
  if (customersQ.isError)
    return <ErrorPanel message={(customersQ.error as ApiError).message} />;
  if (!customer) return <ErrorPanel message="No customers found. Run `make seed`." />;

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Receipt className="h-6 w-6 text-slate-400" />
          {t.payroll_header}
        </h1>
        <p className="mt-1 text-sm text-slate-600">{customer.name}</p>

        <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3">
          <label className="block">
            <span className="block text-xs font-semibold uppercase tracking-widest text-slate-500">
              {t.payroll_period_start}
            </span>
            <input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm shadow-sm focus:border-slate-500 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold uppercase tracking-widest text-slate-500">
              {t.payroll_period_end}
            </span>
            <input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm shadow-sm focus:border-slate-500 focus:outline-none"
            />
          </label>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => runM.mutate()}
              disabled={runM.isPending}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <PlayCircle className="h-4 w-4" />
              {t.payroll_run_button}
            </button>
          </div>
        </div>
      </div>

      {runM.isError ? <ErrorPanel message={runM.error.message} /> : null}
      {runM.data ? (
        <>
          <PayrollSummary run={runM.data} />
          <PayslipsTable payslips={runM.data.payslips} />
        </>
      ) : null}
    </div>
  );
}
